import { Button, Grid } from '@mui/material';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { useSaveData } from '../../../hooks/useSaveData';
import { FileUploaded } from '../../../models/fileUpload';
import { fileUploadApiKeyStore } from '../../../api/file-upload-key-store';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { useDropzone } from 'react-dropzone';
import { useCallback, useState } from 'react';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useAuthorize } from '../../../hooks/use-authorize';

const styling = {
  dropZone: {
    padding: '50px',
    border: '0.1em dashed #99A3A4',
    backgroundColor: '#F2F3F4',
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '4px',
  },
  iconButtonStyle: {
    padding: '15px',
  },
  iconStyle: { width: '40px', height: '40px' },
};

interface DocumentRequestArgs {
  projectID?: number;
  tempEntityID?: number;
}

interface UploadFileComponent {
  onClose: () => void;
  fileData?: any;
  onSaveComplete: (data: any) => void;
  documentRequestArgs: DocumentRequestArgs;
}

export const UploadFileComponent = ({ fileData, onClose, onSaveComplete, documentRequestArgs }: UploadFileComponent) => {
  const [files, setFiles] = useState<any>();
  const form = useReactHookForm({ defaultValues: { ...fileData, ...documentRequestArgs } });
  const { isLoading, mutateAsync } = useSaveData<FileUploaded>(
    fileData ? fileUploadApiKeyStore.updateUploadFile() : fileUploadApiKeyStore.uploadFile());

  const handleDrop = useCallback((acceptedFiles: any) => {
    setFiles(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
  });

  const submitHanlder = async (value: any) => {
    if (files || fileData) {
      const formData = new FormData();
      formData.append('fileID', value?.fileID);
      formData.append('UploadedFile', files);
      formData.append('FileName', files ? files?.name : value?.fileName);
      formData.append('FileType', files ? files?.type : value?.fileType);
      formData.append('Description', value.description ?? '');
      formData.append('FileSection', 'project');
      formData.append('ProjectID', value.projectID);
      const data = await mutateAsync({ data: formData });
      onSaveComplete(data.data);
      form.reset({ fileUpload: [], description: '' });
    }
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <ReactHookForm formName="SEVE_FORM" onSubmit={submitHanlder} formContextProps={form}>
      <LoadingIndicator isLoading={isLoading} />
      <Grid container spacing={2}>
        <Grid item lg={7} sm={7} xl={7} xs={7}>
          <div style={styling.dropZone} {...getRootProps()}>
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Drop the files here ...</p>
            ) : (
              <div>
                {files ? (
                  <p>{`${files.name} are selected to upload`}</p>
                ) : (
                  fileData ?
                    <p>{`${fileData?.fileName} are selected to upload`}</p> :
                    <p>Drag and drop a file here, or click to select a file</p>
                )}
              </div>
            )}
          </div>
        </Grid>
        <Grid item lg={5} sm={5} xl={5} xs={5}>
          <FormInput label="Description" name="description" multiline={true} size={4.5} autoFocus />
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <div className="spaceBetween">
            <Button variant="outlined" style={{ width: '130px' }} onClick={onClose}>
              Cancel
            </Button>
            <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
              <Button type={'submit'} variant="contained" style={{ width: '130px' }} disabled={isReadOnlyPermission}>
                Save
              </Button>
            </GuardedComponent>
          </div>
        </Grid>
      </Grid>
    </ReactHookForm>
  );
};
